import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import {
  useCallback,
  useEffect,
  useRef,
  type RefObject,
} from "react";

const ATTENTION_ROW_ESTIMATE_PX = 96;
const ATTENTION_ROW_OVERSCAN = 6;
// Top padding via the virtualizer so it shares the same coordinate space as rows.
const THREAD_TOP_PADDING_PX = 16;
const THREAD_BOTTOM_STICK_THRESHOLD_PX = 80;
// Re-pin across this window while measurements/async media settle on first paint.
const THREAD_INITIAL_SETTLE_MS = 1200;

export interface UseAttentionThreadScrollOptions {
  rowCount: number;
  getRowKey: (index: number) => string | number;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore?: () => void;
}

export interface UseAttentionThreadScrollResult {
  scrollRef: RefObject<HTMLDivElement>;
  topSentinelRef: RefObject<HTMLDivElement>;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  handleScroll: () => void;
  handleMediaSettled: () => void;
}

export function useAttentionThreadScroll({
  rowCount,
  getRowKey,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: UseAttentionThreadScrollOptions): UseAttentionThreadScrollResult {
  const scrollRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  // One-shot: thread mounts with rows already present, so followOnAppend never
  // sees the 0 -> N growth.
  const didInitialScrollRef = useRef(false);
  // Starts true (open -> newest); flips off when the reader scrolls up.
  const stickToBottomRef = useRef(true);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ATTENTION_ROW_ESTIMATE_PX,
    overscan: ATTENTION_ROW_OVERSCAN,
    paddingStart: THREAD_TOP_PADDING_PX,
    getItemKey: getRowKey,
    anchorTo: "end",
    followOnAppend: "auto",
  });

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || onLoadMore === undefined) return;
    onLoadMore();
  }, [isLoadingMore, onLoadMore]);

  const distanceFromBottom = useCallback((): number | null => {
    const el = scrollRef.current;
    if (el === null) return null;
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  }, []);

  const scrollToBottom = useCallback(() => {
    const count = virtualizer.options.count;
    if (count === 0) return;
    virtualizer.scrollToIndex(count - 1, { align: "end" });
  }, [virtualizer]);

  // Only user scroll flips stickiness; content growth (image loads) must not.
  const handleScroll = useCallback(() => {
    const distance = distanceFromBottom();
    if (distance === null) return;
    stickToBottomRef.current = distance <= THREAD_BOTTOM_STICK_THRESHOLD_PX;
  }, [distanceFromBottom]);

  const handleMediaSettled = useCallback(() => {
    if (stickToBottomRef.current) scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    if (didInitialScrollRef.current || rowCount === 0) return;
    didInitialScrollRef.current = true;
    stickToBottomRef.current = true;

    // rAF settle loop catches cached images that finish before onLoad attaches.
    const deadline = performance.now() + THREAD_INITIAL_SETTLE_MS;
    let frameId = 0;
    const pinToBottom = () => {
      if (stickToBottomRef.current) {
        const distance = distanceFromBottom();
        if (distance === null || distance > 1) scrollToBottom();
      }
      if (performance.now() < deadline) {
        frameId = requestAnimationFrame(pinToBottom);
      }
    };
    pinToBottom();
    return () => cancelAnimationFrame(frameId);
  }, [rowCount, scrollToBottom, distanceFromBottom]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = topSentinelRef.current;
    if (root === null || sentinel === null || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) handleLoadMore();
      },
      { root, rootMargin: "160px 0px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, handleLoadMore]);

  return {
    scrollRef,
    topSentinelRef,
    virtualizer,
    handleScroll,
    handleMediaSettled,
  };
}
